import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs/internal/Subscription';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { SitesService } from 'src/app/sites/sites.service';
import { ExcelService } from 'src/app/shared/excel/excel.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-energy-consumption-charts',
	templateUrl: './energy-consumption-charts.component.html',
})

export class EnergyConsumptionChartsComponent implements OnInit, OnDestroy, OnChanges {

	@Input() device: any;

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();
	getIotemeterEnergyConsumptionSubscription: Subscription = new Subscription();

	readonly allYearsId = 'All-Years';
	readonly allQuartersId = 'All Quarters';
	readonly allMonthsId = 'All-Months';
	readonly allDevicesId = 'All-Devices';

	readonly periods = { MONTHLY: 'monthly', QUARTERLY: 'quarterly' };
	readonly periodTypes = [
		{ label: this.translate.instant('g.quarterly'), value: this.periods.QUARTERLY },
		{ label: this.translate.instant('g.monthly'), value: this.periods.MONTHLY },
	];

	installationDate = null;

	yearList: any[] = [];

	readonly devicesList = [
		{ label: this.translate.instant('iotemeter_site_energy_report.all_meters'), value: this.allDevicesId },
		{ label: this.translate.instant('iotemeter_site_energy_report.edge'), value: DeviceFactory.types.ioTEmeterEdge },
		{ label: this.translate.instant('iotemeter_site_energy_report.pro'), value: DeviceFactory.types.ioTEmeter },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp3'), value: DeviceFactory.types.ioTEmeterSp3 },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp1'), value: DeviceFactory.types.ioTEmeterSp1 },
	];

	selectedYear: string | number;
	selectedPeriodType: string = this.periods.MONTHLY;
	selectedDeviceType: string | number = this.allDevicesId;

	selectedGraphTypes = 'total';
	selectedCharts = 'all';

	chartsData = {
		consumed_energy: [],
		demand_energy: [],
	};

	chartColumns = {
		consumed_energy: [],
		demand_energy: [],
	};

	consumedChartConfig: any = {
		legend: { position: 'top' },
		bar: { groupWidth: 30 },
		tooltip: { isHtml: true },
	};

	demandChartConfig: any = {
		legend: { position: 'top' },
		bar: { groupWidth: 30 },
		tooltip: { isHtml: true },
	};

	constructor(
		private sitesService: SitesService,
		public siteDashboardService: SiteDashboardService,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private excelService: ExcelService,
	) { }

	initializeData() {
		if (this.device?.config_info?.installation_date)
			this.installationDate = moment.utc(this.device.config_info.installation_date * 1000).startOf('day').toDate();

		const { yearList, currentYear } = this.siteDashboardService.prepareYearsList(this.yearList, this.installationDate);
		this.yearList = [{ label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId }, ...yearList];
		this.selectedYear = currentYear;
		this.selectedPeriodType = this.periods.MONTHLY;

		this.getIotemeterEnergyConsumption();
	}

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.currentSite = siteInfo;
				this.installationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.initializeData();
			}
		});
	}

	ngOnChanges(changes) {
		if (changes.device && !changes.device.firstChange) {
			this.device = changes.device.currentValue;
			this.initializeData();
		}
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	getIotemeterEnergyConsumption() {
		const criteria: any = {
			siteId: this.currentSite.id,
			year: this.selectedYear,
			periodType: this.selectedPeriodType,
			quarter: this.allQuartersId,
			month: this.allMonthsId,
			deviceType: this.selectedDeviceType,
		};

		if (this.device)
			criteria.mac_address = this.device.config_info.mac_address;

		this.getIotemeterEnergyConsumptionSubscription = this.sitesService.getIotemeterEnergyConsumption(criteria).subscribe((response: { summary: any }) => {
			this.prepareData(response.summary);
		});
	}

	prepareData(responseData: any) {
		let data;

		switch (true) {
			case this.isAllYearsSelected():
				data = responseData?.years;
				break;
			case this.selectedPeriodType == this.periods.QUARTERLY:
				data = responseData?.quarters;
				break;
			case this.selectedPeriodType == this.periods.MONTHLY:
				data = responseData?.months;
				break;
			default:
				break;
		}

		const consumedChartData = [];
		const demandChartData = [];

		for (const period in data) {
			const periodValue = this.isAllYearsSelected() ? period : this.selectedPeriodType === this.periods.MONTHLY ? moment().month(Number(period) - 1).format('MMMM') : `Q${period}`;
			const consumptionRowValue = [
				periodValue,
				data[period].consumed_energy,
			]

			const demandRowValue = [
				periodValue,
				data[period].peak_energy_demand,
			]

			consumedChartData.push(consumptionRowValue);
			demandChartData.push(demandRowValue);
		}

		this.chartsData.consumed_energy = consumedChartData;
		this.chartsData.demand_energy = demandChartData;

		this.setChartConfig();
	}

	setChartConfig() {
		const periodTitle = `${this.selectedYear == this.allYearsId ? this.translate.instant('g.yearly') : this.selectedPeriodType === this.periods.MONTHLY ? this.translate.instant('g.monthly') : this.translate.instant('g.quarterly')}`;
		this.consumedChartConfig.vAxis = { title: `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.consumption')}`, format: '#\' kWh\'', };
		this.demandChartConfig.vAxis = { title: `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.peak_demand')}`, format: '#\' kW\'', };

		this.chartColumns.consumed_energy = ['', `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.consumption')}`];
		this.chartColumns.demand_energy = ['', `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.peak_demand')}`];
	}

	exportDataToCsv() {
		const periodTitle = `${this.selectedYear == this.allYearsId ? this.translate.instant('g.yearly') : this.selectedPeriodType === this.periods.MONTHLY ? this.translate.instant('g.monthly') : this.translate.instant('g.quarterly')}`;

		const baseHeaders = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device?.config_info?.mac_address,
			includeSpacing: false
		});

		const consumedHeaders = [...baseHeaders, ['', ''], ['', ''], [periodTitle, this.translate.instant('devices.total_kWh')]];
		const demandHeaders = [...baseHeaders, ['', ''], ['', ''], [periodTitle, this.translate.instant('iotemeter_site_energy_report.max_demand_kw')]];

		this.excelService.exportExcel([
			{ data: this.chartsData.consumed_energy, header: consumedHeaders, sheetName: `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.consumption')}` },
			{ data: this.chartsData.demand_energy, header: demandHeaders, sheetName: `${periodTitle} ${this.translate.instant('iotemeter_site_energy_report.peak_demand')}` },
		], `${this.translate.instant('iotemeter_site_energy_report.consumption_charts')}.xlsx`);
	}


	ngOnDestroy() {
		this.siteSubscription?.unsubscribe();
		this.getIotemeterEnergyConsumptionSubscription?.unsubscribe();
	}
}
